import {
  Button,
  Checkbox,
  Toast,
  Tooltip,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { Fragment, useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { ROUTES } from "../../../routes";
import { usePermissions } from "../../../hooks/usePermissions";
import {
  userManagement,
  type User,
  type UserOrganisationWithRoles,
  type UserSibling,
} from "../../../api/userManagement.service";
import LoadingState from "../../../components/LoadingState";
import EmptyState from "../../../components/EmptyState";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import PermissionGate from "../../../components/Permissions/PermissionGate";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal";
import { formatDateTimeShort } from "../../../utils/dateFormatter";
import { rolesData } from "@legal-500/permissions";
import { UserRolesManager } from "./UserRolesManager";
import styles from "../UserManagement.module.css";
import { publicRuntimeConfig } from "../../../config/publicRuntimeConfig";

const STAFF_PLATFORM_ROLES = rolesData
  .filter(role => role.context === "staff")
  .map(role => ({ name: role.name, description: role.description ?? "" }))
  .sort((roleA, roleB) => roleA.name.localeCompare(roleB.name));

/** Account-portal origin for impersonation deep links. Configurable, else derived from the
 * staff-ui origin (staff-… / staff. → account-… / account.). */
function accountUiOrigin(): string {
  const configured = publicRuntimeConfig.accountUiUrl;
  if (configured) return configured.replace(/\/$/, "");
  return window.location.origin.replace(/(\/\/)staff([-.])/, "$1account$2");
}

type UserDetailContext = {
  setToast?: (toast: { message: string; type: ToastType } | null) => void;
};

function contextBadgeLabel(context: User["context"]): string {
  const labels: Record<User["context"], string> = {
    staff: "Staff",
    account: "Account",
  };
  return labels[context] ?? context;
}

function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const outletContext = useOutletContext<UserDetailContext>();
  const { hasPermission } = usePermissions();
  const canManageEntitlements = hasPermission("user.write_accounts");

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [currentRoles, setCurrentRoles] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isSavingRoles, setIsSavingRoles] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const [orgsWithRoles, setOrgsWithRoles] = useState<UserOrganisationWithRoles[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);

  const [siblings, setSiblings] = useState<UserSibling[]>([]);

  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const [isEditingUserInfo, setIsEditingUserInfo] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [isSavingUserInfo, setIsSavingUserInfo] = useState(false);
  const [showRemoveExternalIdModal, setShowRemoveExternalIdModal] = useState(false);
  const [orgToRemove, setOrgToRemove] = useState<UserOrganisationWithRoles | null>(null);

  useEffect(() => {
    if (id) {
      void loadUser(id);
      void loadUserOrganisations(id);
    }
  }, [id]);

  useEffect(() => {
    if (user?.email) {
      void loadSiblings(user.email, user.id);
    }
  }, [user?.email, user?.id]);

  async function loadUser(userId: string) {
    try {
      setLoading(true);
      setError(null);
      const [userData, roleData] = await Promise.all([
        userManagement.getUserById(userId),
        userManagement.getUserPlatformRoles(userId),
      ]);
      setUser(userData);
      setCurrentRoles(roleData);
      setSelectedRoles(roleData);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load user"));
    } finally {
      setLoading(false);
    }
  }

  async function loadUserOrganisations(userId: string) {
    try {
      setOrgsLoading(true);
      const orgs = await userManagement.getUserOrganisations(userId);
      setOrgsWithRoles(orgs);
    } catch (err) {
      showToast(
        `Failed to load organisations: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setOrgsLoading(false);
    }
  }

  async function loadSiblings(email: string, currentUserId: string) {
    try {
      const allRows = await userManagement.getUserSiblingsByEmail(email);
      setSiblings(allRows.filter(row => row.id !== currentUserId));
    } catch {
      // sibling links are best-effort; failures don't block the view
    }
  }

  function showToast(message: string, type: ToastType) {
    setToast({ message, type });
    outletContext.setToast?.({ message, type });
  }

  function handleRoleToggle(role: string) {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(existingRole => existingRole !== role) : [...prev, role],
    );
  }

  async function handleSaveRoles() {
    if (!id) return;
    setIsSavingRoles(true);
    try {
      await userManagement.setUserPlatformRoles(id, "staff", selectedRoles);
      setCurrentRoles(selectedRoles);
      showToast("Roles updated successfully", "success");
    } catch (err) {
      showToast(
        `Failed to update roles: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setIsSavingRoles(false);
    }
  }

  async function handleImpersonate() {
    if (!user) return;
    setIsImpersonating(true);
    try {
      const { token, expiresAt } = await userManagement.mintImpersonationToken(user.id);
      const url = `${accountUiOrigin()}/impersonate#token=${encodeURIComponent(
        token,
      )}&expiresAt=${encodeURIComponent(expiresAt)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      showToast(
        `Failed to start impersonation: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setIsImpersonating(false);
    }
  }

  function startEditingUserInfo() {
    setEditedName(user?.name ?? "");
    setIsEditingUserInfo(true);
  }

  function cancelEditingUserInfo() {
    setIsEditingUserInfo(false);
    setEditedName("");
  }

  async function handleSaveUserName() {
    if (!id || !user) return;
    setIsSavingUserInfo(true);
    try {
      const updatedUser = await userManagement.updateUser(id, { name: editedName });
      setUser(updatedUser);
      setIsEditingUserInfo(false);
      showToast("User name updated", "success");
    } catch (err) {
      showToast(
        `Failed to update user: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setIsSavingUserInfo(false);
    }
  }

  async function handleRemoveExternalId() {
    if (!id) return;
    const updatedUser = await userManagement.updateUser(id, { externalId: null });
    setUser(updatedUser);
    setShowRemoveExternalIdModal(false);
    showToast("External ID removed", "success");
  }

  async function handleRemoveOrgMembership() {
    if (!id || !orgToRemove) return;
    await userManagement.removeOrganisationMember(orgToRemove.id, id);
    setOrgsWithRoles(prev => prev.filter(org => org.id !== orgToRemove.id));
    setOrgToRemove(null);
    showToast(`Removed from ${orgToRemove.name}`, "success");
  }

  if (loading) return <LoadingState />;

  if (error) {
    const status = (error as Error & { status?: number }).status;
    if (status === 401 || status === 403) return <UnauthorizedView />;
    return (
      <EmptyState
        variant="error"
        title="Failed to load"
        message={`Failed to load user: ${error.message}`}
      />
    );
  }

  if (!user) return <EmptyState variant="empty" title="Not found" message="User not found" />;

  const rolesChanged =
    JSON.stringify([...selectedRoles].sort()) !== JSON.stringify([...currentRoles].sort());

  return (
    <div className={styles.detailWrapper}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle} style={{ margin: 0 }}>
            User Info
          </h2>
          <PermissionGate permission="user.write_accounts">
            {isEditingUserInfo ? (
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => void handleSaveUserName()}
                  disabled={
                    isSavingUserInfo || editedName.trim() === "" || editedName === user.name
                  }
                >
                  {isSavingUserInfo ? "Saving…" : "Save"}
                </Button>
                <Button variant="outlined" color="default" onClick={cancelEditingUserInfo}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button variant="outlined" color="default" onClick={startEditingUserInfo}>
                Edit
              </Button>
            )}
          </PermissionGate>
        </div>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Name</span>
            {isEditingUserInfo ? (
              <input
                type="text"
                value={editedName}
                onChange={e => setEditedName(e.target.value)}
                className={styles.inlineInput}
                aria-label="Name"
              />
            ) : (
              <span className={styles.infoValue}>{user.name}</span>
            )}
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Email</span>
            <span className={styles.infoValue}>{user.email}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Status</span>
            <span
              className={`${styles.statusBadge} ${
                user.status === "verified"
                  ? styles.statusVerified
                  : user.status === "suspended"
                    ? styles.statusSuspended
                    : styles.statusPending
              }`}
            >
              {user.status}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Type</span>
            <span className={styles.infoValue}>{user.type}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Context</span>
            <span className={styles.contextBadge}>{contextBadgeLabel(user.context)}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Created</span>
            <span className={styles.infoValue}>{formatDateTimeShort(user.createdAt)}</span>
          </div>
          {user.externalId && (
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>External ID</span>
              <span className={styles.infoValue}>{user.externalId}</span>
              <PermissionGate permission="user.write_accounts">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setShowRemoveExternalIdModal(true)}
                >
                  Remove
                </Button>
              </PermissionGate>
            </div>
          )}
        </div>

        {siblings.length > 0 && (
          <div className={styles.siblingLinks}>
            <span className={styles.infoLabel}>Also exists as</span>
            {siblings.map(sibling => (
              <span key={sibling.id} className={styles.siblingLink}>
                <Link to={ROUTES.users.detail(sibling.id)}>
                  {contextBadgeLabel(sibling.context)} ({sibling.status})
                </Link>
              </span>
            ))}
          </div>
        )}
      </div>

      {user.context === "staff" && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Platform Roles</h2>
          <div className={styles.rolesGrid}>
            {STAFF_PLATFORM_ROLES.map(role => (
              <Tooltip
                key={role.name}
                title={role.name}
                content={role.description || "(no description)"}
                position="right"
              >
                <label className={styles.checkboxRow}>
                  <Checkbox
                    checked={selectedRoles.includes(role.name)}
                    onChange={() => handleRoleToggle(role.name)}
                    label={role.name}
                  />
                </label>
              </Tooltip>
            ))}
          </div>
          <PermissionGate permission="user.write_accounts">
            <div className={styles.formActions}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => void handleSaveRoles()}
                disabled={isSavingRoles || !rolesChanged}
              >
                {isSavingRoles ? "Saving..." : "Save Roles"}
              </Button>
            </div>
          </PermissionGate>
        </div>
      )}

      {user.context === "account" && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Organisation Memberships</h2>
          {orgsLoading ? (
            <LoadingState />
          ) : orgsWithRoles.length > 0 ? (
            <table className={styles.rolesTable}>
              <thead>
                <tr>
                  <th>Organisation</th>
                  <th>Roles</th>
                  <PermissionGate permission="user.write_accounts">
                    <th>Actions</th>
                  </PermissionGate>
                </tr>
              </thead>
              <tbody>
                {orgsWithRoles.map(org => (
                  <Fragment key={org.id}>
                    <tr>
                      <td>
                        <Link to={ROUTES.users.organisationDetail(org.id)}>{org.name}</Link>
                      </td>
                      <td>
                        {org.roles.length > 0 ? (
                          <div className={styles.permissionList}>
                            {org.roles.map(role => (
                              <span key={role} className={styles.permissionTag}>
                                {role}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className={styles.infoLabel}>None</span>
                        )}
                      </td>
                      <PermissionGate permission="user.write_accounts">
                        <td>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={e => {
                              e.stopPropagation();
                              setOrgToRemove(org);
                            }}
                          >
                            Remove
                          </Button>
                        </td>
                      </PermissionGate>
                    </tr>
                    {canManageEntitlements && (
                      <tr>
                        <td colSpan={3}>
                          <UserRolesManager
                            userId={user.id}
                            organisationId={org.id}
                            organisationName={org.name}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState
              variant="empty"
              title="No memberships"
              message="No organisation memberships found."
            />
          )}
        </div>
      )}

      {user.context === "account" && (
        <PermissionGate permission="user_management.impersonate">
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Support tools</h2>
            <p
              style={{
                color: "var(--color-text-secondary)",
                fontSize: "var(--font-size-sm)",
                marginBottom: "0.75rem",
              }}
            >
              Open the account portal as this user (impersonation). Your staff identity is recorded
              on the session and a banner shows in the portal. The session is short-lived (30 min)
              and ends when the tab is closed. If the user belongs to multiple firms you choose
              which firm in the portal, the same as they would.
            </p>
            <Button
              variant="outlined"
              color="default"
              onClick={() => void handleImpersonate()}
              disabled={isImpersonating}
            >
              {isImpersonating ? "Opening…" : "Open account portal as this user"}
            </Button>
          </div>
        </PermissionGate>
      )}

      {showRemoveExternalIdModal && (
        <ConfirmationModal
          title="Remove external ID"
          message="This will unlink the Frontegg identity from this account. The account can be re-linked later."
          warningMessage="Removing the external ID means this account will no longer be connected to the Frontegg user."
          confirmButtonText="Remove"
          confirmButtonColor="error"
          onClose={() => setShowRemoveExternalIdModal(false)}
          onConfirm={handleRemoveExternalId}
        />
      )}

      {orgToRemove && (
        <ConfirmationModal
          title="Remove member"
          message={`Remove this user from "${orgToRemove.name}"?`}
          warningMessage="The user will lose access and their roles in this firm will be deleted."
          confirmButtonText="Remove"
          confirmButtonColor="error"
          onClose={() => setOrgToRemove(null)}
          onConfirm={handleRemoveOrgMembership}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default UserDetail;
